import useAxios from '../hooks/useAxios';

/**
 * Get forming base information.
 * @param {Object} data - The data object containing the following properties:
 * @param {string} data.transId - 移轉單號
 * @param {CancelToken} cancelToken - The cancel token.
 * @param {function} onSuccess - The function to call when the request is successful.
 * @param {function} onError - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getFormingBase(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios('/getFormingBase', data, true, cancelToken);
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Get forming date selection.
 * @param {Object} data - The data object containing the following properties:
 * @param {string} data.transId - 移轉單號
 * @param {CancelToken} cancelToken - The cancel token.
 * @param {function} onSuccess - The function to call when the request is successful.
 * @param {function} onError - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getFormingDateSelection(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios('/getFormingDate', data, true, cancelToken);
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Get QC detect information.
 * @param {Object} data - The data object containing the following properties:
 * @param {string} data.page - 頁碼
 * @param {CancelToken} cancelToken - The cancel token.
 * @param {function} onSuccess - The function to call when the request is successful.
 * @param {function} onError - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getQCDetect(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios('/qcDetect', data, true, cancelToken);
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Get forming check information.
 * @param {Object} data - The data object containing the following properties:
 * @param {string} data.transId - 移轉單號
 * @param {string} data.formCheckId - 壓型檢查ID
 * @param {CancelToken} cancelToken - The cancel token.
 * @param {function} onSuccess - The function to call when the request is successful.
 * @param {function} onError - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getFormingCheck(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios('/getFormingCheck', data, true, cancelToken);
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Set forming check information.
 * @param {Object} data - The data object containing the following properties:
 * @param {string} data.transId - 移轉單號
 * @param {Object} data.formCheck - 壓型檢測設定
 * @param {string} data.formCheck.formCheckId - 壓型檢查ID
 * @param {string} data.formCheck.heightUpper - 壓高上限
 * @param {string} data.formCheck.heightLower - 壓高下限
 * @param {string} data.formCheck.weightUpper - 重量上限
 * @param {string} data.formCheck.weightLower - 重量下限
 * @param {string} data.formCheck.knifeCount - 刀數
 * @param {string} data.formCheck.formModuler - 裝模人員ID
 * @param {string} data.formCheck.speed - 轉速
 * @param {number} data.formCheck.measureDateTimestamp - 量測時間戳記
 * @param {string} data.formCheck.formModel - 模具
 * @param {string} data.formCheck.isPass - 壓型結果
 * @param {string} data.formCheck.checkComment - 檢查備註
 * @param {string} data.formCheck.formMachineId - 機台ID
 * @param {string} data.formCheck.bucketNo - 桶號
 * @param {string} data.formCheck.formUser - 壓型人員
 * @param {string} data.formCheck.machineName - 機台名稱
 * @param {Array} data.formDetail - 壓型檢測詳細
 * @param {string} data.formDetail.formDetailId - 壓型檢測詳細ID
 * @param {string} data.formDetail.formModel - 模具
 * @param {string} data.formDetail.knifeCount - 刀數
 * @param {string} data.formDetail.formHeight - 壓高
 * @param {string} data.formDetail.formWeight - 重量
 * @param {string} data.formDetail.detailComments - 檢查備註
 * @param {Array} data.formDetail.checkDetectList - 檢查缺陷列表
 * @param {string} data.formDetail.checkDetectList.detectId - 檢查缺陷ID
 * @param {boolean} data.formDetail.checkDetectList.isPromise - 是否勾選
 * @param {CancelToken} cancelToken - The cancel token.
 * @param {function} onSuccess - The function to call when the request is successful.
 * @param {function} onError - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function formCheckSetting(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios('/formCheckSetting', data, true, cancelToken);
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Get sinter base information.
 * @param {Object} data - The data object containing the following properties:
 * @param {string} data.transId - 移轉單號
 * @param {number} data.styleId - 量測型態（e.g. 尺寸、電性等）
 * @param {number} data.sizeType - 1. 巡檢 2. 尺寸
 * @param {CancelToken} cancelToken - The cancel token.
 * @param {function} onSuccess - The function to call when the request is successful.
 * @param {function} onError - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getSinterBase(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios('/getSinterBase', data, true, cancelToken);
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Get sinter check information.
 * @param {number} styleId - 量測型態（e.g. 尺寸、電性等）
 * @param {number} sizeType - 1. 巡檢 2. 尺寸
 * @param {string} [mergedCheckIds] - 合併檢查ID
 * @param {string} [sinterCheckId] - 燒結檢查ID
 * @param {string} [position] - 位置（僅巡檢時才有，sizeType === 1）
 * @param {number} [level] - 層數（僅巡檢時才有，sizeType === 1）
 * @param {CancelToken} cancelToken - The cancel token.
 * @param {function} onSuccess - The function to call when the request is successful.
 * @param {function} onError - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getSinterCheck(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode, noPrivilegeMsg] = await useAxios(
    '/getSinterCheck',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode, noPrivilegeMsg);
  }
}

/**
 * Save sinter check setting data.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing sinter check information.
 * @param {Object} params.data.sinterCheck - The sinter check information.
 * @param {string} [params.data.sinterCheck.mergedCheckIds] - 合併檢查ID（多個移轉單時使用）
 * @param {string} [params.data.sinterCheck.sinterCheckId] - 燒結檢查ID（單個移轉單時使用）
 * @param {string} params.data.sinterCheck.transId - 移轉單號
 * @param {string} params.data.sinterCheck.elecStoveNo - 爐號（ID）
 * @param {string} params.data.sinterCheck.machineName - 爐號（顯示編號）
 * @param {string} [params.data.sinterCheck.elSizeMergeNo] - 電性尺寸合併編號（多個移轉單時使用）
 * @param {string} [params.data.sinterCheck.sinterPosition] - 燒結位置（僅巡檢時使用）
 * @param {number} [params.data.sinterCheck.sinterLevel] - 燒結層數（僅巡檢時使用）
 * @param {number} [params.data.sinterCheck.mergeQuantity] - 合併數量（多個移轉單時使用）
 * @param {number} [params.data.sinterCheck.transQuantity] - 移轉數量（單個移轉單時使用）
 * @param {number} [params.data.sinterCheck.isPass] - 是否通過（巡檢時使用）
 * @param {string} [params.data.sinterCheck.checkComment] - 檢查備註（巡檢時使用）
 * @param {number} [params.data.sinterCheck.isPassSize] - 尺寸是否通過（尺寸檢測時使用）
 * @param {string} [params.data.sinterCheck.sizeComment] - 尺寸備註（尺寸檢測時使用）
 * @param {number} [params.data.sinterCheck.isPassElec] - 電性是否通過（電性檢測時使用）
 * @param {string} [params.data.sinterCheck.elecComment] - 電性備註（電性檢測時使用）
 * @param {Array} params.data.sinterRow - 燒結行數據陣列
 * @param {Object} params.data.sinterRow[].rowMapperId - 行映射ID
 * @param {string} params.data.sinterRow[].sinterCheckId - 燒結檢查ID
 * @param {number} params.data.sinterRow[].sizeType - 尺寸類型（1: 巡檢, 2: 尺寸, 3: 電性）
 * @param {number} params.data.sinterRow[].styleId - 量測型態ID
 * @param {number} params.data.sinterRow[].sinterRowId - 燒結行ID
 * @param {Array} params.data.sinterRow[].checkDetailList - 檢查詳細列表
 * @param {number} params.data.sinterRow[].checkDetailList[].sinterDetailId - 燒結詳細ID
 * @param {number} params.data.sinterRow[].checkDetailList[].sinterRowId - 燒結行ID
 * @param {string} params.data.sinterRow[].checkDetailList[].measureId - 量測項目ID
 * @param {string} params.data.sinterRow[].checkDetailList[].detailValue - 詳細數值
 * @param {Array} params.data.sinterRow[].checkDetailList[].measureConfig - 量測配置
 * @param {number} [params.data.sinterRow[].isPass] - 該行是否通過（-1: 空行, 0: 不通過, 1: 通過）
 * @param {Array} [params.data.sinterRow[].checkDetectList] - 檢查缺陷列表（非電性檢測時使用）
 * @param {string} params.data.sinterRow[].checkDetectList[].detectId - detect 項目ID（e.g. 前缺、後缺）
 * @param {boolean} params.data.sinterRow[].checkDetectList[].isPromise - 是否勾選
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function sinterCheckSetting(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode, noPrivilegeMsg] = await useAxios(
    '/sinterCheckSetting',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode, noPrivilegeMsg);
  }
}

/**
 * Delete transfer QC data.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {string} params.data.transId - 移轉單號
 * @param {number} params.data.sizeType - 1. 巡檢 2. 尺寸 3. 電性
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {string} params.data.sinterCheckId - 燒結檢查ID
 * @param {number} params.data.rowMapperId - 行映射ID
 * @param {string} params.data.position - 位置（僅巡檢時才有，sizeType === 1）
 * @param {number} params.data.level - 層數（僅巡檢時才有，sizeType === 1）
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function deleteTransferQcData(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode, noPrivilegeMsg] = await useAxios(
    '/deleteTransferQcData',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode, noPrivilegeMsg);
  }
}

/**
 * Get sinter check cpk information.
 * @param {Object} data - The data object containing the following properties:
 * @param {string} data.rowMapperId - 行映射ID
 * @param {number} data.styleId - 量測型態（e.g. 尺寸、電性等）
 * @param {number} data.sizeType - 1. 巡檢 2. 尺寸
 * @param {CancelToken} cancelToken - The cancel token.
 * @param {function} onSuccess - The function to call when the request is successful.
 * @param {function} onError - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getSinterCheckCpk(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const searchParams = new URLSearchParams();
  searchParams.append('rowMapperId', data.rowMapperId);
  searchParams.append('styleId', data.styleId);
  searchParams.append('sizeType', data.sizeType);
  const [status, result, rtCode, noPrivilegeMsg] = await useAxios(
    `/v2/sinter-check-cpk/list?${searchParams.toString()}`,
    {},
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode, noPrivilegeMsg);
  }
}

/**
 * Set sinter check cpk information.
 * @param {Object} data - The data object containing the following properties:
 * @param {string} data.level - 層數
 * @param {number} data.position - 位置
 * @param {number} data.styleId - 量測型態（e.g. 尺寸、電性等）
 * @param {number} data.sizeType - 1. 巡檢 2. 尺寸
 * @param {string} data.sinterCheckId - 燒結檢查ID
 * @param {string} data.elSizeMergeNo - 電性尺寸合併編號
 * @param {Array} data.qcCpkCreateList - 檢測資料Cpk列表
 * @param {string} data.qcCpkCreateList[].measureId - 量測項目ID
 * @param {string} data.qcCpkCreateList[].cpkValue - CPK值
 * @param {string} data.qcCpkCreateList[].isNormalDist - 是否為常態分布
 * @param {CancelToken} cancelToken - The cancel token.
 * @param {function} onSuccess - The function to call when the request is successful.
 * @param {function} onError - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function setSinterCheckCpk(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode, noPrivilegeMsg] = await useAxios(
    '/v2/sinter-check-cpk',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode, noPrivilegeMsg);
  }
}

/**
 * Get QC export detail.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {Array} params.data.qc - The QC data.
 * @param {number} params.data.isExcel - isExcel = 1 時，表示要匯出Excel
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getQCExportDetail(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode, noPrivilegeMsg] = await useAxios(
    '/qcExportDetail',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode, noPrivilegeMsg);
  }
}

/**
 * Get QC export advanced selection.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getQCExportAdvancedSelection(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode, noPrivilegeMsg] = await useAxios(
    '/qcExportAdvanced',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode, noPrivilegeMsg);
  }
}

/**
 * Get QC export result list.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getQCExportResultList(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode, noPrivilegeMsg] = await useAxios(
    '/qcCheckExport',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode, noPrivilegeMsg);
  }
}
